import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import { apolloClient } from '../../apollo/client';
import { buscarAtletas } from '../../services/atletaService';
import { TORNEIOS_FILTRADOS, TORNEIO_POR_ID, TorneiosFiltradosData, TorneiosFiltradosVars, TorneioPorIdData, TorneioPorIdVars } from '../../services/torneioService';
import type { PaymentsFilter, StatusPagamento } from '../../types/pagamento';

export type AvailableFilterKey = 'atleta' | 'torneio' | 'categoria' | 'data' | 'status';

export interface PaymentsTableToolbarProps {
  availableFilters?: AvailableFilterKey[];
  initialFilters?: PaymentsFilter;
  value?: PaymentsFilter;
  onChange?: (filters: PaymentsFilter) => void;
  onApply?: (filters: PaymentsFilter) => void;
  onReset?: () => void;
  disabled?: boolean;
  placeholders?: Partial<{
    atleta: string;
    torneio: string;
    categoria: string;
    status: string;
    dataInicio: string;
    dataFim: string;
  }>;
  statusOptions?: { label: string; value: StatusPagamento }[];
  fetchAtletas?: (q: string) => Promise<Array<{ id: string; label: string }>>;
  fetchTorneios?: (q: string) => Promise<Array<{ id: string; label: string }>>;
  fetchCategorias?: (q: string, torneioId?: string | null) => Promise<Array<{ id: string; label: string }>>;
  showRefreshButton?: boolean;
  onRefresh?: () => void;
}

const DEFAULT_AVAILABLE: AvailableFilterKey[] = ['atleta','torneio','categoria','data','status'];
const DEFAULT_STATUS_OPTIONS: { label: string; value: StatusPagamento }[] = [
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Pago', value: 'PAGO' },
  { label: 'Cancelado', value: 'CANCELADO' },
];

const useDebounced = (fn: (...args: any[]) => void, delay: number) => {
  const ref = useRef<number | undefined>(undefined);
  const cb = useCallback((...args: any[]) => {
    if (ref.current) window.clearTimeout(ref.current);
    ref.current = window.setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
  useEffect(() => () => { if (ref.current) window.clearTimeout(ref.current); }, []);
  return cb;
};

const PaymentsTableToolbar: React.FC<PaymentsTableToolbarProps> = ({
  availableFilters = DEFAULT_AVAILABLE,
  initialFilters,
  value,
  onChange,
  onApply,
  onReset,
  disabled,
  placeholders,
  statusOptions = DEFAULT_STATUS_OPTIONS,
  fetchAtletas,
  fetchTorneios,
  fetchCategorias,
  showRefreshButton,
  onRefresh,
}) => {
  const isControlled = !!value;
  const [filters, setFilters] = useState<PaymentsFilter>(initialFilters || {});
  useEffect(() => { if (isControlled && value) setFilters(value); }, [isControlled, value]);

  const [atletaInput, setAtletaInput] = useState('');
  const [torneioInput, setTorneioInput] = useState('');
  const [categoriaInput, setCategoriaInput] = useState('');
  const [loadingAtleta, setLoadingAtleta] = useState(false);
  const [loadingTorneio, setLoadingTorneio] = useState(false);
  const [loadingCategoria, setLoadingCategoria] = useState(false);
  const [atletaOptions, setAtletaOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [torneioOptions, setTorneioOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [categoriaOptions, setCategoriaOptions] = useState<Array<{ id: string; label: string }>>([]);
  // Falha de busca remota de cada autocomplete. Uma rejeicao (rede fora, resposta malformada)
  // antes escapava do fetch debounced como unhandled rejection e derrubava o teste/render que
  // estivesse em andamento. Agora e capturada -- e EXIBIDA no proprio campo, nunca engolida
  // (docs/anti-patterns.md: sem supressao silenciosa de erro).
  const [atletaError, setAtletaError] = useState<string | null>(null);
  const [torneioError, setTorneioError] = useState<string | null>(null);
  const [categoriaError, setCategoriaError] = useState<string | null>(null);
  const lastSyncedTorneioLabel = useRef<string | null>(null);

  const emitChange = useCallback((next: PaymentsFilter) => {
    if (onChange) onChange(next);
    if (!isControlled) setFilters(next);
  }, [isControlled, onChange]);

  const doFetchAtletas = useCallback(async (q: string) => {
    const fetcher = fetchAtletas || (async (query: string) => {
      const list = await buscarAtletas(query);
      if (!Array.isArray(list)) {
        throw new Error('Resposta invalida da busca de atletas (lista ausente).');
      }
      return list.map(a => ({ id: a.id, label: a.usuario?.nome || 'Sem nome' }));
    });
    setLoadingAtleta(true);
    setAtletaError(null);
    try {
      const items = await fetcher(q);
      setAtletaOptions(items ?? []);
    } catch (error) {
      console.error('[PaymentsTableToolbar] falha ao buscar atletas', error);
      setAtletaOptions([]);
      setAtletaError('Falha ao buscar atletas. Tente novamente.');
    } finally {
      setLoadingAtleta(false);
    }
  }, [fetchAtletas]);

  const doFetchTorneios = useCallback(async (q: string) => {
    const fetcher = fetchTorneios || (async (query: string) => {
      const { data } = (await apolloClient.query<TorneiosFiltradosData, TorneiosFiltradosVars>({
        query: TORNEIOS_FILTRADOS,
        variables: { 
          nome: query || null, 
          ativo: true,
          page: 0, 
          size: 10,
          direction: 'ASC',
          sortBy: 'dataInicio',
        },
        fetchPolicy: 'no-cache',
      })) ?? ({} as { data?: TorneiosFiltradosData });
      if (!data?.torneiosFiltrados) {
        throw new Error('Resposta invalida da busca de torneios (torneiosFiltrados ausente).');
      }
      return (data.torneiosFiltrados.items || []).map(t => ({ id: t.id, label: t.nome }));
    });
    setLoadingTorneio(true);
    setTorneioError(null);
    try {
      const items = await fetcher(q);
      setTorneioOptions(items ?? []);
    } catch (error) {
      console.error('[PaymentsTableToolbar] falha ao buscar torneios', error);
      setTorneioOptions([]);
      setTorneioError('Falha ao buscar torneios. Tente novamente.');
    } finally {
      setLoadingTorneio(false);
    }
  }, [fetchTorneios]);

  const doFetchCategorias = useCallback(async (q: string, torneioId?: string | null) => {
    const fetcher = fetchCategorias || (async (_query: string, tid?: string | null) => {
      if (!tid) return [] as Array<{id:string;label:string}>;
      const { data } = (await apolloClient.query<TorneioPorIdData, TorneioPorIdVars>({
        query: TORNEIO_POR_ID,
        variables: { id: tid },
        fetchPolicy: 'no-cache',
      })) ?? ({} as { data?: TorneioPorIdData });
      if (!data?.torneioPorId) {
        throw new Error('Resposta invalida da busca de categorias (torneioPorId ausente).');
      }
      const cats = data.torneioPorId.categoriasTorneio || [];
      return cats.map(c => ({ id: c.id, label: c.nome }));
    });
    setLoadingCategoria(true);
    setCategoriaError(null);
    try {
      const items = await fetcher(q, torneioId);
      setCategoriaOptions(items ?? []);
    } catch (error) {
      console.error('[PaymentsTableToolbar] falha ao buscar categorias', error);
      setCategoriaOptions([]);
      setCategoriaError('Falha ao buscar categorias. Tente novamente.');
    } finally {
      setLoadingCategoria(false);
    }
  }, [fetchCategorias]);

  const debouncedAtleta = useDebounced(doFetchAtletas, 350);
  const debouncedTorneio = useDebounced(doFetchTorneios, 350);
  const debouncedCategoria = useDebounced((q: string) => doFetchCategorias(q, filters.torneioId), 350);

  useEffect(() => { if (availableFilters.includes('atleta')) debouncedAtleta(atletaInput); }, [atletaInput]);
  useEffect(() => { if (availableFilters.includes('torneio')) debouncedTorneio(torneioInput); }, [torneioInput]);
  useEffect(() => { if (availableFilters.includes('categoria')) debouncedCategoria(categoriaInput); }, [categoriaInput, filters.torneioId]);
  useEffect(() => {
    if (!availableFilters.includes('torneio')) return;
    const selectedId = filters.torneioId || null;
    if (!selectedId) {
      if (lastSyncedTorneioLabel.current && torneioInput === lastSyncedTorneioLabel.current) {
        setTorneioInput('');
        lastSyncedTorneioLabel.current = null;
      }
      return;
    }
    const alreadyLoaded = torneioOptions.some((opt) => opt.id === selectedId);
    if (alreadyLoaded) {
      const option = torneioOptions.find((opt) => opt.id === selectedId);
      if (option) {
        if (!torneioInput) {
          lastSyncedTorneioLabel.current = option.label;
          setTorneioInput(option.label);
        } else if (torneioInput === option.label) {
          lastSyncedTorneioLabel.current = option.label;
        }
      }
      return;
    }

    let active = true;
    (async () => {
      try {
        const { data } = await apolloClient.query<TorneioPorIdData, TorneioPorIdVars>({
          query: TORNEIO_POR_ID,
          variables: { id: selectedId },
          fetchPolicy: 'no-cache',
        });
        if (!active) return;
        const torneio = data?.torneioPorId;
        if (!torneio) {
          // A query respondeu, mas o torneio selecionado nao existe mais (id obsoleto vindo do
          // estado do pai / da URL). O filtro CONTINUA valendo na consulta, entao ficar calado
          // deixaria o usuario vendo um campo vazio sobre uma lista ja filtrada.
          setTorneioError('Torneio selecionado nao encontrado. Escolha outro.');
          return;
        }
        const option = { id: torneio.id, label: torneio.nome };
        setTorneioOptions((prev) => {
          if (prev.some((item) => item.id === option.id)) {
            return prev;
          }
          return [...prev, option];
        });
        setTorneioInput((current) => {
          if (current) {
            return current;
          }
          lastSyncedTorneioLabel.current = option.label;
          return option.label;
        });
      } catch (error) {
        // Sem supressao silenciosa (ai/anti-patterns.md secao 4): falhar aqui deixa o campo em
        // branco enquanto o filtro por torneio SEGUE ativo na consulta -- o usuario veria uma
        // lista filtrada sem nenhuma pista do porque. O motivo vai para o mesmo canal das buscas
        // remotas deste campo. Guardado por `active`: efeito cancelado nao mexe em estado.
        if (!active) return;
        console.error('[PaymentsTableToolbar] falha ao carregar o torneio selecionado', error);
        setTorneioError('Falha ao carregar o torneio selecionado. Tente novamente.');
      }
    })();

    return () => { active = false; };
  }, [availableFilters, filters.torneioId, torneioInput, torneioOptions]);

  const hasDateError = useMemo(() => {
    if (!filters?.dataInicio || !filters?.dataFim) return false;
    const a = dayjs(filters.dataInicio);
    const b = dayjs(filters.dataFim);
    if (!a.isValid() || !b.isValid()) return false;
    return a.isAfter(b);
  }, [filters?.dataInicio, filters?.dataFim]);

  const handleReset = () => {
    const next = initialFilters || {};
    emitChange(next);
    if (onReset) onReset();
  };

  const p = {
    atleta: placeholders?.atleta ?? 'Buscar atleta',
    torneio: placeholders?.torneio ?? 'Buscar torneio',
    categoria: placeholders?.categoria ?? 'Buscar categoria',
    status: placeholders?.status ?? 'Selecione o status',
    dataInicio: placeholders?.dataInicio ?? 'Data início',
    dataFim: placeholders?.dataFim ?? 'Data fim',
  };

  const showAtleta = availableFilters.includes('atleta');
  const showTorneio = availableFilters.includes('torneio');
  const showCategoria = availableFilters.includes('categoria');
  const showStatus = availableFilters.includes('status');
  const showData = availableFilters.includes('data');

  return (
    <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
      {showAtleta && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <Autocomplete
              options={atletaOptions}
              getOptionLabel={(o) => o.label}
              loading={loadingAtleta}
              disabled={disabled}
              inputValue={atletaInput}
              onInputChange={(_, v) => setAtletaInput(v)}
              value={atletaOptions.find(o => o.id === filters.atletaId) || null}
              onChange={(_, v) => emitChange({ ...filters, atletaId: v?.id || null })}
              renderInput={(params) => (
                <TextField {...params} label={p.atleta} size="small" error={!!atletaError} helperText={atletaError ?? ' '} InputProps={{ ...params.InputProps, endAdornment: (<>{loadingAtleta ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
              )}
            />
          </Box>
        </Box>
      )}

      {(showTorneio || showCategoria || showStatus) && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
          {showTorneio && (
            <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
              <Autocomplete
                options={torneioOptions}
                getOptionLabel={(o) => o.label}
                loading={loadingTorneio}
                disabled={disabled}
                inputValue={torneioInput}
                onInputChange={(_, v) => setTorneioInput(v)}
                value={torneioOptions.find(o => o.id === filters.torneioId) || null}
                onChange={(_, v) => {
                  const next = { ...filters, torneioId: v?.id || null, categoriaId: null };
                  emitChange(next);
                }}
                renderInput={(params) => (
                  <TextField {...params} label={p.torneio} size="small" error={!!torneioError} helperText={torneioError ?? ' '} InputProps={{ ...params.InputProps, endAdornment: (<>{loadingTorneio ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Box>
          )}

          {showCategoria && (
            <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
              <Autocomplete
                options={categoriaOptions}
                getOptionLabel={(o) => o.label}
                loading={loadingCategoria}
                disabled={disabled || !filters.torneioId}
                inputValue={categoriaInput}
                onInputChange={(_, v) => setCategoriaInput(v)}
                value={categoriaOptions.find(o => o.id === filters.categoriaId) || null}
                onChange={(_, v) => emitChange({ ...filters, categoriaId: v?.id || null })}
                renderInput={(params) => (
                  <TextField {...params} label={p.categoria} size="small" error={!!categoriaError} helperText={categoriaError ?? ' '} InputProps={{ ...params.InputProps, endAdornment: (<>{loadingCategoria ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) }} />
                )}
              />
            </Box>
          )}

          {showStatus && (
            <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
              <Select
                size="small"
                fullWidth
                value={(filters.status as any) || ''}
                onChange={(e: SelectChangeEvent) => emitChange({ ...filters, status: (e.target.value || null) as StatusPagamento | null })}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{p.status}</em>
                </MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      )}

      {showData && (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label={p.dataInicio}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataInicio || ''}
              onChange={(e) => emitChange({ ...filters, dataInicio: e.target.value || null })}
              error={hasDateError}
              helperText=' '
            />
          </Box>
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label={p.dataFim}
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataFim || ''}
              onChange={(e) => emitChange({ ...filters, dataFim: e.target.value || null })}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : ' '}
            />
          </Box>
        </Box>
      )}

      <Box display="flex" gap={1}>
        <Button variant="contained" color="primary" size="small" disabled={disabled || hasDateError} onClick={() => onApply && onApply(filters)}>Aplicar</Button>
        <Button variant="outlined" color="inherit" size="small" disabled={disabled} onClick={handleReset}>Limpar</Button>
        {showRefreshButton && onRefresh && (
          <Button variant="outlined" color="inherit" size="small" disabled={disabled} onClick={onRefresh}>Atualizar</Button>
        )}
      </Box>
    </Box>
  );
};

export default PaymentsTableToolbar;

